import React, { useContext, useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography } from "@mui/material";
import { useWindowSize } from "../../../../shared/hooks/useWindowSize";
import { useTranslation } from "react-i18next";
import filterContext from "../../../../app/providers/filter/filterContext";
import { useDispatch } from "react-redux";
import { setDraft } from "../../../../app/store/reservationSlice";
import env from "../../../../app/config/envHelper";
import AddIcon from "@mui/icons-material/Add";
import { altChooseButtonStyleTurquoise, altCloseButtonStyleDark } from "../../../../shared/styles/buttonStyles";
import { dialogStyle } from "../../../../shared/styles/dialogStyles";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useGetWorkshopServicesQuery } from "../../../../app/store/api/workshopApi";
import { skipToken } from "@reduxjs/toolkit/query";

const SelectedServices = ({ draft }) => {
  const { isMobile } = useWindowSize();
  const { i18n, t } = useTranslation();
  const [ basicServices, setBasicServices ] = useState([]);
  const [ extraServices, setExtraServices ] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const { state: filterState } = useContext(filterContext);
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const {data:services} = useGetWorkshopServicesQuery(draft?.workshop?.id ?? skipToken);

  const selectServicesFromFilter = () => {
    let selectedServices = [];
    filterState?.filter?.forEach((filterItem) => {
      filterItem.services.forEach((service) => {
        if (service.active) {
          selectedServices.push(service);
        }
      });
    });
    dispatch(setDraft({ services: selectedServices }));
  };

  useEffect(() => {
    if (services) {
      setBasicServices(services.filter((service) => service.level === "basic"));
      setExtraServices(services.filter((service) => service.level === "extra"));
    }
  },[services])

  useEffect(() => {
    if (!draft?.services || draft?.services?.length === 0) {
      selectServicesFromFilter();
    }
  }, []);

  const serviceFontStyleHeader = { fontWeight: 500, fontSize: "0.875rem" };
  const serviceFontStyleDescription = { opacity: 0.8, mt: 0.5, fontSize: "0.75rem" };
  const fontColor = { color: "#003C59" };

  const handleDialogOpen = () => {
    const idsFromDraft = new Set((draft.services || []).map((s) => s.id));
    setSelectedIds(idsFromDraft);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleApply = () => {
    const newServices = [...basicServices,...extraServices].filter((service) => selectedIds.has(service.id));
    dispatch(setDraft({ services: newServices }));
    setDialogOpen(false);
  };

  const handleServiceClick = (service) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(service.id)) {
        next.delete(service.id);
      } else {
        next.add(service.id);
      }
      return next;
    });
  };

  const handleDeleteService = (service) => {
    dispatch(setDraft({ services: draft.services.filter((s) => s.id !== service.id) }));
  };

  const displayServices = useMemo(()=>{
    return draft.services.filter((service) => service.category !== "extra");
  },[draft.services])

  return (
    <>
      <Grid container spacing={2} sx={{ display: "flex", flexDirection: "column", marginTop: "20px", border: "1px solid #bbdefb", borderRadius: "5px", backgroundColor: "#dcffff", padding: "10px" }}>
        <Box>
          <Typography sx={{ fontWeight: "bold", margin: 0, fontSize: isMobile && "0.75rem", color: "#003C59"}}>{t('baseServices.selectedServices')}</Typography>
        </Box>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "10px" }}>
          <Box sx={{ width: "100%" }}>
              <Grid container spacing={1}>
                {displayServices && displayServices?.length > 0 && displayServices?.map((service) => (
                  <Grid key={service.id}>
                    <Chip label={service.name || service.translations?.[i18n.language]?.name || service.id} sx={{  backgroundColor: "transparent", border: "1px solid grey", borderRadius: "5px" }} onDelete={() => handleDeleteService(service)} />
                  </Grid>
                ))}
                <Chip label={t('baseServices.addService')} clickable sx={{  backgroundColor: "transparent", border: "1px solid grey", borderRadius: "5px" }} icon={<AddIcon />} onClick={handleDialogOpen} />
              </Grid>
          </Box>
        </Box>
      </Grid>

      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth='sm' fullWidth sx={{ ...dialogStyle }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <Box>
            <Typography variant='h6' sx={{ fontWeight: "bold", ...fontColor }}>
              {t("filter.availableServices")}
            </Typography>
            <Typography variant='body2' sx={{ color: "text.secondary", marginTop: 1, ...fontColor }}>
              {t("filter.availableServicesDescription")}
            </Typography>
          </Box>
          <IconButton onClick={handleDialogClose} sx={{ padding: 0, marginTop: "-4px" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: isMobile ? "16px" : "36px" }}>
          {basicServices?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' sx={{ ...fontColor, mb: 2, fontWeight: "bold" }}>
                {t("filter.baseServices")}
              </Typography>
              {basicServices?.map((service) => {
                const isActive = selectedIds.has(service.id);
                return (
                  <Alert
                    key={service.id}
                    icon={isActive ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
                    sx={{
                      backgroundColor: isActive ? "rgb(214, 252, 255)" : "transparent",
                      color: "#003C59",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      marginBottom: "12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: isActive ? "rgb(150, 229, 242)" : "#f5f5f5",
                      },
                      "& .MuiAlert-icon": {
                        color: "#003C59",
                        alignSelf: "center",
                      },
                      "& .MuiAlert-message": {
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        width: "100%",
                      },
                    }}
                    onClick={() => handleServiceClick(service)}
                  >
                    <Typography variant='body1' sx={serviceFontStyleHeader}>
                      {service.translations[i18n.language]?.name || service.translations.fi.name}
                    </Typography>
                    <Typography variant='body2' sx={serviceFontStyleDescription}>
                      {service.translations[i18n.language]?.description || service.translations.fi.description}
                    </Typography>
                  </Alert>
                );
              })}
            </Box>
          )}

          {extraServices?.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant='h6' sx={{ ...fontColor, mb: 2, fontWeight: "bold" }}>
                {t("filter.uncommonServices")}
              </Typography>
              {extraServices?.map((service) => {
                const isActive = selectedIds.has(service.id);
                return (
                  <Alert
                    key={service.id}
                    icon={isActive ? <CheckCircleOutlineIcon /> : <RadioButtonUncheckedIcon />}
                    sx={{
                      backgroundColor: isActive ? "rgb(214, 252, 255)" : "transparent",
                      color: "#003C59",
                      border: "1px solid #e0e0e0",
                      borderRadius: "4px",
                      marginBottom: "12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: isActive ? "rgb(150, 229, 242)" : "#f5f5f5",
                      },
                      "& .MuiAlert-icon": {
                        color: "#003C59",
                        alignSelf: "center",
                      },
                      "& .MuiAlert-message": {
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "flex-start",
                        width: "100%",
                      },
                    }}
                    onClick={() => handleServiceClick(service)}
                  >
                    <Typography variant='body1' sx={serviceFontStyleHeader}>
                      {service.translations[i18n.language]?.name || service.translations.fi.name}
                    </Typography>
                    <Typography variant='body2' sx={serviceFontStyleDescription}>
                      {service.translations[i18n.language]?.description || service.translations.fi.description}
                    </Typography>
                  </Alert>
                );
              })}
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            backgroundColor: "#003C59",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            padding: "16px 24px",
            gap: 2,
            zIndex: 1,
          }}
        >
          <Button onClick={handleDialogClose} variant='outlined' sx={{ ...altCloseButtonStyleDark, flex: 1 }}>
            {t("common.close")}
          </Button>
          <Button onClick={handleApply} variant='contained' sx={{ ...altChooseButtonStyleTurquoise, flex: 1 }}>
            {t("common.apply")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SelectedServices;